import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AppLayout from '@amzn/meridian/app-layout';
import Masthead, { MastheadTitle, MastheadMenuButton } from '@amzn/meridian/masthead';
import SideMenu, { SideMenuLink, SideMenuTitle } from '@amzn/meridian/side-menu';
import Heading from '@amzn/meridian/heading';
import Row from '@amzn/meridian/row';
import Icon from '@amzn/meridian/icon';
import Text from '@amzn/meridian/text';
import Toggle from '@amzn/meridian/toggle';
import asleepTokens from '@amzn/meridian-tokens/base/icon/asleep';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { setAuthToken } from '../services/api';

interface PageLayoutProps {
    children: React.ReactNode;
}

const DS_INPUTS_PATH = '/delivery-stations/inputs';

const PageLayout: React.FC<PageLayoutProps> = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const path = location.pathname;
    const { mode, setMode } = useTheme();
    const { user, token } = useAuth();
    const [sideMenuOpen, setSideMenuOpen] = useState(true);
    const [openLinkHash, setOpenLinkHash] = useState<string | null>('#delivery-stations');

    useEffect(() => {
        if (token) setAuthToken(token);
    }, [token]);

    useEffect(() => {
        if ([DS_INPUTS_PATH, '/'].includes(path)) setOpenLinkHash('#delivery-stations');
    }, [path]);

    return (
        <AppLayout
            headerComponent={Masthead}
            sidebarComponent={SideMenu}
            spacingInset="450"
            backgroundColor="principal"
        >
            <Masthead size="medium">
                <MastheadMenuButton onClick={() => setSideMenuOpen(!sideMenuOpen)} aria-expanded={sideMenuOpen} />
                <MastheadTitle href="/" onClick={(href: string) => navigate(href)}>
                    Bogyo
                </MastheadTitle>
            </Masthead>
            <div style={{ position: 'fixed', top: '0', right: '16px', zIndex: 1000, display: 'flex', alignItems: 'center', gap: '12px', height: '48px' }}>
                {user && (
                    <Row spacing="300" alignmentVertical="center">
                        <Text>Welcome, {user}</Text>
                        <img
                            src={`https://internal-cdn.amazon.com/badgephotos.amazon.com/?uid=${user}`}
                            alt={user}
                            style={{ width: '28px', height: '28px', borderRadius: '50%' }}
                            onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                        />
                    </Row>
                )}
                <Row spacing="200" alignmentVertical="center">
                    <Toggle
                        checked={mode === 'dark'}
                        onChange={(checked: boolean) => setMode(checked ? 'dark' : 'light')}
                        aria-label="Toggle dark mode"
                    />
                    <Icon tokens={asleepTokens} color="primary" />
                </Row>
            </div>

            <SideMenu open={sideMenuOpen} onClose={() => setSideMenuOpen(false)}>
                <SideMenuTitle>
                    <Heading level={5}>Bogyo</Heading>
                </SideMenuTitle>

                <SideMenuLink
                    selected={[DS_INPUTS_PATH, '/'].includes(path)}
                    open={openLinkHash === '#delivery-stations'}
                    onClick={() => {
                        setOpenLinkHash(openLinkHash === '#delivery-stations' ? null : '#delivery-stations');
                    }}
                >
                    Delivery Stations
                    <SideMenuLink
                        href={DS_INPUTS_PATH}
                        selected={path === DS_INPUTS_PATH || path === '/'}
                        onClick={(href: string) => navigate(href)}
                        onEscape={() => setOpenLinkHash(null)}
                    >
                        Inputs
                    </SideMenuLink>
                </SideMenuLink>
            </SideMenu>

            {children}
        </AppLayout>
    );
};

export default PageLayout;
